import React, { FunctionComponent, FormEvent, useState, useEffect, createRef, } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Button,
  Grid,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  LoadingButton,
} from '@mui/lab'
import { AppDispatch, RootState } from '../../store/store';
import { code } from './forgot-password-code';
import { feedback } from '../../components/Feedback/feedback';
import AuthLayout from '../../layouts/AuthLayout/AuthLayout';
import { useTranslation } from 'react-i18next';
import { push } from '@lagunovsky/redux-react-router';
import PasswordPolicy from '../../components/PasswordPolicy/PasswordPolicy';

import './ForgotPasswordCode.css';
import useTitle from '../../helpers/use-title';
import useValidator from '../../helpers/hooks/use-validator';

const ForgotPasswordCode: FunctionComponent = () => {

  const { t } = useTranslation( [ 'forgotPasswordCode' ] );

  const dispatch = useDispatch<AppDispatch>();
  const { code:param } = useParams<{ code: string }>();
  const url = new URL( location.href );
  const searchParam = url.searchParams.get( 'username' ) || '';

  const newPasswordRef = createRef<HTMLInputElement>();
  const repeatPasswordRef = createRef<HTMLInputElement>();

  useValidator( [ newPasswordRef, ] )
    .required()
    .passwordPolicy();

  const loading = useSelector( ( state: RootState ) => state.pages.forgotPasswordCode.loading );
  useTitle( t( 'reset-your-password' ) );

  const onSubmit = ( event: FormEvent ) => {
    event.preventDefault();

    const target = event.target as HTMLFormElement;
    const data = Object.fromEntries(new FormData(target));

    if( data.newPassword !== data.repeatPassword ) {
      return dispatch( feedback( {message: t( 'passwords-do-not-match' ) } ) )
    }

    dispatch( code( {
      username: data.username as string,
      code: data.code as string,
      password: data.newPassword as string,
    } ) ).then( ( action ) => {

      if( action.payload ) {
        dispatch( push( '/forgot-password/success' ) );
      }
    })
  }

  const [showNewPassword, setShowNewPassword] = useState(false);
  const handleClickShowNewPassword = () => setShowNewPassword(!showNewPassword);

  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const handleClickShowRepeatPassword = () => setShowRepeatPassword(!showRepeatPassword );

  return (
    <AuthLayout language={false} header={ true }>
      <div className="page page-forgot-password-code">
        <form onSubmit={ onSubmit }>
          <h2>{ t( 'reset-your-password' ) }</h2>
          <span>{ t('think-about-a-new-password-and-enter-it' ) }</span>
          <Grid item xs={12}>
            <PasswordPolicy />
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <input type="hidden"
                name="username"
                value={ searchParam }
              ></input>
            </Grid>
            <Grid item xs={12}>
              <input type="hidden"
                name="code"
                value={ param }
              ></input>
            </Grid>
            <Grid item xs={12}>
              <TextField
                type={ showNewPassword ? "text" : "password"}
                label={ t( 'new-password' ) }
                inputRef={ newPasswordRef }
                name="newPassword"
                variant='standard'
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowNewPassword}
                      >
                        {showNewPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                type={showRepeatPassword ? "text" : "password"}
                label={ t( 'repeat-password' ) }
                inputRef={ repeatPasswordRef }
                name="repeatPassword"
                variant='standard'
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowRepeatPassword}
                      >
                        {showRepeatPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <LoadingButton
                loading={ loading === 'pending' }
                fullWidth
                variant='contained'
                type="submit"
              >
              { t( 'reset-password' ) }
              </LoadingButton>
            </Grid>

          </Grid>
        </form>
      </div>
    </AuthLayout>
  );
}

export default ForgotPasswordCode;
